/**
 * ============================================================
 * FILM INTRO CONTROLLER
 * Cinematografische intro met scene management
 * ============================================================
 */

class FilmIntroController {
  constructor(container) {
    this.container = container;
    this.scenes = container.querySelectorAll('.film-intro__scene');
    this.currentSceneIndex = 0;
    this.totalScenes = this.scenes.length;
    this.isPlaying = true;
    this.isPaused = false;
    this.sceneTimelines = [];
    
    // Settings from data attributes
    this.transitionType = container.dataset.sceneTransition || 'crossfade';
    this.autoAdvance = container.dataset.autoAdvance !== 'false';
    this.loop = container.dataset.loop === 'true';
    
    // Elements
    this.progressBar = container.querySelector('.film-intro__progress-bar');
    this.skipButton = container.querySelector('.film-intro__skip');
    this.audioElement = document.getElementById('film-intro-audio');
    this.audioToggle = container.querySelector('.film-intro__audio-toggle');
    
    // Audio state
    this.isMuted = true;
    this.audioReady = false;
    
    // Bind methods
    this.nextScene = this.nextScene.bind(this);
    this.skipIntro = this.skipIntro.bind(this);
    this.handleKeyboard = this.handleKeyboard.bind(this);
    this.toggleAudio = this.toggleAudio.bind(this);
    
    this.init();
  }
  
  init() {
    console.log(`🎬 Film Intro: ${this.totalScenes} scenes`);
    
    // Setup skip button
    if (this.skipButton) {
      this.skipButton.addEventListener('click', this.skipIntro);
    }
    
    // Setup audio
    this.initAudio();
    
    // Keyboard controls
    document.addEventListener('keydown', this.handleKeyboard);
    
    // Check for audio overlay first
    this.audioOverlay = document.getElementById('audio-overlay');
    if (this.audioOverlay) {
      this.showAudioOverlay();
    } else {
      // Start first scene immediately
      this.startIntro();
    }
  }
  
  showAudioOverlay() {
    // Pause intro until user makes a choice
    this.isPaused = true;
    
    const enableBtn = document.getElementById('enable-audio-btn');
    const skipBtn = document.getElementById('skip-audio-btn');
    
    if (enableBtn) {
      enableBtn.addEventListener('click', () => {
        this.hideOverlayAndStart(true);
      });
    }
    
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        this.hideOverlayAndStart(false);
      });
    }
  }
  
  hideOverlayAndStart(enableAudio) {
    if (this.audioOverlay) {
      this.audioOverlay.classList.add('hidden');
    }
    
    if (enableAudio && this.audioElement) {
      this.audioElement.play().then(() => {
        this.isMuted = false;
        if (this.audioToggle) {
          this.audioToggle.setAttribute('data-muted', 'false');
        }
        // Fade in audio
        gsap.fromTo(this.audioElement, 
          { volume: 0 }, 
          { volume: parseFloat(this.audioElement.dataset.volume) || 0.3, duration: 2 }
        );
        console.log('🎵 Audio enabled');
      }).catch(err => {
        console.log('🎵 Audio blocked:', err);
      });
    }
    
    // Start the intro
    this.isPaused = false;
    this.startIntro();
  }
  
  startIntro() {
    if (this.totalScenes > 0) {
      this.playScene(0);
    } else {
      this.complete();
    }
  }
  
  initAudio() {
    if (!this.audioElement) return;
    
    // Set initial volume
    const volume = parseFloat(this.audioElement.dataset.volume) || 0.3;
    this.audioElement.volume = volume;
    
    // Audio toggle button
    if (this.audioToggle) {
      this.audioToggle.addEventListener('click', this.toggleAudio);
    }
  }
  
  toggleAudio() {
    if (!this.audioElement) return;
    
    if (this.isMuted) {
      // Unmute and play
      this.audioElement.play().then(() => {
        this.isMuted = false;
        if (this.audioToggle) {
          this.audioToggle.setAttribute('data-muted', 'false');
        }
        console.log('🎵 Audio playing');
        
        // Fade in audio
        gsap.fromTo(this.audioElement, 
          { volume: 0 }, 
          { volume: parseFloat(this.audioElement.dataset.volume) || 0.3, duration: 1 }
        );
      }).catch(err => {
        console.log('🎵 Audio blocked:', err);
      });
    } else {
      // Mute with fade out
      gsap.to(this.audioElement, {
        volume: 0,
        duration: 0.5,
        onComplete: () => {
          this.audioElement.pause();
          this.isMuted = true;
          if (this.audioToggle) {
            this.audioToggle.setAttribute('data-muted', 'true');
          }
          console.log('🎵 Audio muted');
        }
      });
    }
  }
  
  fadeOutAudio() {
    if (this.audioElement && !this.isMuted) {
      gsap.to(this.audioElement, {
        volume: 0,
        duration: 2,
        onComplete: () => {
          this.audioElement.pause();
        }
      });
    }
  }
  
  playScene(index) {
    if (index >= this.totalScenes) {
      if (this.loop) {
        this.playScene(0);
      } else {
        this.complete();
      }
      return;
    }
    
    const scene = this.scenes[index];
    const prevScene = index > 0 ? this.scenes[index - 1] : null;
    
    // Scene data
    const duration = parseFloat(scene.dataset.sceneDuration) || 3;
    const delay = parseFloat(scene.dataset.sceneDelay) || 0;
    const transitionIn = scene.dataset.sceneTransitionIn || 'fade';
    const transitionOut = scene.dataset.sceneTransitionOut || 'fade';
    const easing = scene.dataset.sceneEasing || 'power2.inOut';
    const sceneType = scene.dataset.sceneType;
    
    console.log(`🎬 Scene ${index + 1}/${this.totalScenes}: ${sceneType} (${duration}s)`);
    
    // Create timeline for this scene
    const tl = gsap.timeline({
      onComplete: () => {
        if (this.autoAdvance && this.isPlaying) {
          this.nextScene();
        }
      }
    });
    
    // Transition out previous scene
    if (prevScene) {
      tl.add(this.createTransitionOut(prevScene, transitionOut, easing), 0);
    }
    
    // Transition in current scene
    tl.add(this.createTransitionIn(scene, transitionIn, easing), delay);
    
    // Animate scene content
    tl.add(this.animateSceneContent(scene, sceneType), delay + 0.3);
    
    // Background Ken Burns (supports both image and video)
    const bgElement = scene.querySelector('.scene__bg-image') || scene.querySelector('.scene__bg-video');
    if (bgElement && bgElement.dataset.kenburns === 'true') {
      const scale = parseFloat(bgElement.dataset.kenburnsScale) || 1.1;
      tl.to(bgElement, {
        scale: scale,
        duration: duration + 2,
        ease: 'none'
      }, delay);
    }
    
    // Progress bar
    if (this.progressBar) {
      const totalDuration = this.calculateTotalDuration();
      const elapsed = this.calculateElapsedDuration(index);
      tl.to(this.progressBar, {
        width: `${((elapsed + duration) / totalDuration) * 100}%`,
        duration: duration,
        ease: 'none'
      }, delay);
    }
    
    // Wait for scene duration then continue
    tl.to({}, { duration: duration }, delay);
    
    this.currentSceneIndex = index;
    this.sceneTimelines.push(tl);
  }
  
  createTransitionIn(scene, type, easing) {
    const tl = gsap.timeline();
    const bg = scene.querySelector('.scene__bg-image') || scene.querySelector('.scene__bg-video');

    // Ensure video playback starts
    const video = scene.querySelector('.scene__bg-video');
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }

    // Reset scene
    gsap.set(scene, { visibility: 'visible' });

    switch (type) {
      case 'fade':
        tl.fromTo(scene, { opacity: 0 }, { opacity: 1, duration: 0.8, ease: easing });
        if (bg) tl.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 1 }, 0);
        break;

      case 'slide-up':
        tl.fromTo(scene, { opacity: 0, y: 100 }, { opacity: 1, y: 0, duration: 0.8, ease: easing });
        if (bg) tl.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 0.8 }, 0);
        break;

      case 'slide-down':
        tl.fromTo(scene, { opacity: 0, y: -100 }, { opacity: 1, y: 0, duration: 0.8, ease: easing });
        if (bg) tl.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 0.8 }, 0);
        break;

      case 'zoom-in':
        tl.fromTo(scene, { opacity: 0, scale: 1.2 }, { opacity: 1, scale: 1, duration: 1, ease: easing });
        if (bg) tl.fromTo(bg, { opacity: 0, scale: 1.3 }, { opacity: 1, scale: 1, duration: 1.2 }, 0);
        break;

      case 'blur-in':
        tl.fromTo(scene, { opacity: 0, filter: 'blur(20px)' }, { opacity: 1, filter: 'blur(0px)', duration: 1, ease: easing });
        if (bg) tl.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 1 }, 0);
        break;

      default:
        tl.to(scene, { opacity: 1, duration: 0.5 });
        if (bg) tl.to(bg, { opacity: 1, duration: 0.8 }, 0);
    }

    return tl;
  }
  
  createTransitionOut(scene, type, easing) {
    const tl = gsap.timeline();
    
    switch (type) {
      case 'fade':
        tl.to(scene, { opacity: 0, duration: 0.5, ease: easing });
        break;
        
      case 'slide-up':
        tl.to(scene, { opacity: 0, y: -50, duration: 0.5, ease: easing });
        break;
        
      case 'slide-down':
        tl.to(scene, { opacity: 0, y: 50, duration: 0.5, ease: easing });
        break;
        
      case 'zoom-out':
        tl.to(scene, { opacity: 0, scale: 0.9, duration: 0.5, ease: easing });
        break;
        
      case 'blur-out':
        tl.to(scene, { opacity: 0, filter: 'blur(10px)', duration: 0.5, ease: easing });
        break;
        
      default:
        tl.to(scene, { opacity: 0, duration: 0.3 });
    }
    
    tl.set(scene, { visibility: 'hidden' });
    
    return tl;
  }
  
  animateSceneContent(scene, type) {
    const tl = gsap.timeline();
    
    switch (type) {
      case 'logo_reveal':
        tl.add(this.animateLogoReveal(scene));
        break;
        
      case 'text_reveal':
        tl.add(this.animateTextReveal(scene));
        break;
        
      case 'quote':
        tl.add(this.animateQuote(scene));
        break;
        
      case 'countdown':
        tl.add(this.animateCountdown(scene));
        break;
        
      case 'image_focus':
        // No content animation for pure image
        const caption = scene.querySelector('.scene__caption');
        if (caption) {
          tl.fromTo(caption, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 }, 1);
        }
        break;
    }
    
    return tl;
  }
  
  animateLogoReveal(scene) {
    const tl = gsap.timeline();
    
    const preText = scene.querySelector('.scene__pre-text');
    const letters = scene.querySelectorAll('.scene__letter');
    const tagline = scene.querySelector('.scene__tagline');
    const line = scene.querySelector('.scene__line');
    const subtitle = scene.querySelector('.scene__subtitle');
    const letterDelay = parseFloat(scene.querySelector('[data-letter-delay]')?.dataset.letterDelay) || 0.05;
    
    // Pre text
    if (preText) {
      tl.to(preText, { opacity: 1, y: 0, duration: 0.4 }, 0);
    }
    
    // Letters one by one
    letters.forEach((letter, i) => {
      tl.to(letter, {
        opacity: 1,
        y: 0,
        duration: 0.15,
        ease: 'power2.out'
      }, 0.3 + (i * letterDelay));
    });
    
    // Tagline
    if (tagline) {
      tl.to(tagline, { opacity: 1, duration: 0.5 }, letters.length * letterDelay + 0.5);
    }
    
    // Line
    if (line) {
      tl.to(line, { scaleX: 1, duration: 0.6, ease: 'power2.inOut' }, '-=0.2');
    }
    
    // Subtitle
    if (subtitle) {
      tl.to(subtitle, { opacity: 1, duration: 0.4 }, '-=0.1');
    }
    
    return tl;
  }
  
  animateTextReveal(scene) {
    const tl = gsap.timeline();
    
    const heading = scene.querySelector('.scene__heading');
    const body = scene.querySelector('.scene__body');
    const headingAnim = heading?.dataset.animate || 'fade-up';
    
    if (heading) {
      switch (headingAnim) {
        case 'split-lines':
          // Split into lines and animate
          const text = heading.textContent;
          heading.innerHTML = `<span class="line-inner">${text}</span>`;
          tl.fromTo(heading.querySelector('.line-inner'), 
            { y: '100%', opacity: 0 }, 
            { y: '0%', opacity: 1, duration: 0.8, ease: 'power3.out' }
          );
          break;
          
        case 'typewriter':
          tl.add(this.typewriterEffect(heading));
          break;
          
        case 'blur-in':
          tl.fromTo(heading, 
            { opacity: 0, filter: 'blur(15px)' }, 
            { opacity: 1, filter: 'blur(0px)', duration: 1 }
          );
          break;
          
        default:
          tl.fromTo(heading, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6 });
      }
    }
    
    if (body) {
      tl.fromTo(body, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.5 }, '-=0.2');
    }
    
    return tl;
  }
  
  animateQuote(scene) {
    const tl = gsap.timeline();
    
    const quote = scene.querySelector('.scene__quote');
    const author = scene.querySelector('.scene__quote-author');
    const speed = parseInt(quote?.dataset.speed) || 50;
    
    if (quote) {
      tl.add(this.typewriterEffect(quote, speed));
    }
    
    if (author) {
      tl.to(author, { opacity: 1, duration: 0.4 }, '-=0.3');
    }
    
    return tl;
  }
  
  animateCountdown(scene) {
    const tl = gsap.timeline();
    
    const container = scene.querySelector('.scene__countdown');
    const numberEl = scene.querySelector('.scene__countdown-number');
    const progress = scene.querySelector('.scene__countdown-progress');
    const start = parseInt(container?.dataset.start) || 5;
    
    // Animate circle
    if (progress) {
      tl.to(progress, {
        strokeDashoffset: 0,
        duration: start,
        ease: 'linear'
      }, 0);
    }
    
    // Countdown numbers
    for (let i = start; i >= 1; i--) {
      tl.call(() => {
        if (numberEl) numberEl.textContent = i;
      }, null, start - i);
      
      // Pulse animation
      tl.fromTo(numberEl, 
        { scale: 1.2, opacity: 0.5 }, 
        { scale: 1, opacity: 1, duration: 0.3 }, 
        start - i
      );
    }
    
    return tl;
  }
  
  typewriterEffect(element, speed = 50) {
    const tl = gsap.timeline();
    const text = element.textContent;
    element.textContent = '';
    element.style.opacity = 1;
    
    const chars = text.split('');
    chars.forEach((char, i) => {
      tl.call(() => {
        element.textContent += char;
      }, null, i * (speed / 1000));
    });
    
    return tl;
  }
  
  nextScene() {
    this.playScene(this.currentSceneIndex + 1);
  }
  
  previousScene() {
    if (this.currentSceneIndex > 0) {
      // Kill current timeline
      if (this.sceneTimelines.length > 0) {
        this.sceneTimelines[this.sceneTimelines.length - 1].kill();
      }
      this.playScene(this.currentSceneIndex - 1);
    }
  }
  
  skipIntro() {
    console.log('⏭️ Skipping intro');
    
    // Kill all timelines
    this.sceneTimelines.forEach(tl => tl.kill());
    
    // Complete immediately
    this.complete();
  }
  
  pause() {
    this.isPaused = true;
    this.sceneTimelines.forEach(tl => tl.pause());
  }
  
  resume() {
    this.isPaused = false;
    this.sceneTimelines.forEach(tl => tl.resume());
  }
  
  handleKeyboard(e) {
    switch (e.key) {
      case 'Escape':
        this.skipIntro();
        break;
      case ' ':
        e.preventDefault();
        this.isPaused ? this.resume() : this.pause();
        break;
      case 'ArrowRight':
        this.nextScene();
        break;
      case 'ArrowLeft':
        this.previousScene();
        break;
    }
  }
  
  calculateTotalDuration() {
    let total = 0;
    this.scenes.forEach(scene => {
      total += parseFloat(scene.dataset.sceneDuration) || 3;
    });
    return total;
  }
  
  calculateElapsedDuration(upToIndex) {
    let elapsed = 0;
    for (let i = 0; i < upToIndex; i++) {
      elapsed += parseFloat(this.scenes[i].dataset.sceneDuration) || 3;
    }
    return elapsed;
  }
  
  complete() {
    console.log('✅ Film intro complete');
    this.isPlaying = false;
    
    // Fade out audio
    this.fadeOutAudio();
    
    // Cleanup
    document.removeEventListener('keydown', this.handleKeyboard);
    
    // Dispatch event
    this.container.dispatchEvent(new CustomEvent('introComplete'));
    
    // Trigger global scroll system
    if (typeof startScrollSystem === 'function') {
      startScrollSystem();
    }
    
    // Show scroll hint
    const scrollHint = document.getElementById('scroll-hint');
    if (scrollHint) {
      gsap.to(scrollHint, { opacity: 1, visibility: 'visible', duration: 0.5 });
    }
  }
  
  destroy() {
    this.sceneTimelines.forEach(tl => tl.kill());
    document.removeEventListener('keydown', this.handleKeyboard);
    if (this.skipButton) {
      this.skipButton.removeEventListener('click', this.skipIntro);
    }
  }
}

// Initialize when DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const filmIntro = document.getElementById('film-intro');
  if (filmIntro && filmIntro.classList.contains('film-intro')) {
    // Check if it's the new scene-based system
    if (filmIntro.dataset.totalScenes) {
      window.filmIntroController = new FilmIntroController(filmIntro);
    }
  }
});